import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatActualTarget,
  isTargetedRow,
  type UnitsPreference,
  type WeeklyTargetRow,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { IncompleteNote, incompleteWord } from "./IncompleteNote";
import { makeMeterRowShellStyle } from "./meterSharedStyles";
import {
  TargetBar,
  ZONE_GLYPH,
  noTargetWord,
  zoneTextStyle,
  zoneWord,
} from "./TargetBar";
import { weightUnitLabels } from "../weightUnitLabels";

// <TargetMeterRow> — one row of a target meter: the label, the actual/target
// value pair, the zone word+glyph, and the <TargetBar> underneath. Lifted at
// AP-3b's 2nd callsite (the in-workout strip) out of Home/WeeklyTargetsCard so
// the weekly Home card and the in-workout strip render an identical row from
// one place. The caller resolves the label (muscle name vs exercise name) and
// passes the row; everything else — value formatting, the never-colour-only
// zone glyph+word, the composed screen-reader label, the bar — lives here.

export type TargetMeterRowProps = {
  theme: UiTheme;
  row: WeeklyTargetRow;
  // Caller-resolved display label (muscleKey(...) for a muscle row, the
  // catalog name for an exercise row) — the primitive is axis-agnostic.
  label: string;
  units: UnitsPreference;
  locale: string;
  // Scale for an untargeted (neutral) bar — the caller's busiest untargeted
  // row. Ignored for targeted rows (they scale to their own goal).
  degradedMax?: number;
};

export function TargetMeterRow({
  theme,
  row,
  label,
  units,
  locale,
  degradedMax,
}: TargetMeterRowProps) {
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);
  const { actual: actualStr, target: targetStr } = formatActualTarget(
    row,
    units,
    locale,
    unitLabels,
  );
  const stateWord =
    row.zone != null
      ? zoneWord(t, row.zone)
      : !isTargetedRow(row)
        ? noTargetWord(t)
        : null;
  // One composed label so a screen reader announces the row as a single unit:
  // done ("8 / 12") and goal-state ("building").
  const a11yLabel = [
    label,
    targetStr != null ? `${actualStr} / ${targetStr}` : actualStr,
    stateWord,
    row.incomplete ? incompleteWord(t) : null,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <View>
      <View accessible accessibilityLabel={a11yLabel}>
        <View style={styles.rowTop}>
          <Text style={styles.pattern}>
            {label}
          </Text>
          <View style={styles.valueWrap}>
            <Text style={styles.value}>
              {actualStr}
              {targetStr != null ? (
                <Text style={styles.valueTarget}>
                  {" / "}
                  {targetStr}
                </Text>
              ) : null}
            </Text>
            {row.zone != null ? (
              <Text style={[styles.state, zoneTextStyle(theme, row.zone)]}>
                {ZONE_GLYPH[row.zone]} {zoneWord(t, row.zone)}
              </Text>
            ) : !isTargetedRow(row) ? (
              <Text style={[styles.state, styles.stateNoTarget]}>{noTargetWord(t)}</Text>
            ) : null}
          </View>
        </View>
        <TargetBar
          theme={theme}
          actual={row.actual}
          target={row.target}
          bandLow={row.bandLow}
          bandHigh={row.bandHigh}
          zone={row.zone}
          degradedMax={degradedMax}
        />
      </View>
      {/* Sibling of the accessible block: its pill must stay reachable. */}
      {row.incomplete ? <IncompleteNote theme={theme} reasons={row.incompleteReasons} /> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    // Shell da meterSharedStyles, una sola definizione: era condivisa con la
    // riga della banda (tolta in PG-64 fetta 2), e le righe di misura devono
    // leggersi come lo stesso oggetto.
    ...makeMeterRowShellStyle(theme),
    pattern: {
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "500",
    },
    // Value on top, state word under it, right-aligned and never shrunk: at 1.5x
    // font scale (fr/de) the label wraps instead of being squeezed to "Ischio-ja…".
    valueWrap: {
      flexDirection: "column",
      alignItems: "flex-end",
      flexShrink: 0,
      maxWidth: "60%",
    },
    valueTarget: { color: p.ink3 },
    // #393: words only, no glyph (no zone to mark).
    stateNoTarget: { color: p.ink2 },
  });
}
